import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/api";
import { Card, Spinner } from "@/components/ui-kit";
import { EmptyState, ProBadge } from "@/components/dashboard-bits";
import { useAuth } from "@/auth";
import { Send, ArrowLeft } from "lucide-react";
import { fmtTime, fmtConvoTime, dateSeparatorLabel, isNewDay } from "@/lib/time";

const MAX_LEN = 2000;
const initials = (n) => (n || "?").slice(0, 2).toUpperCase();

function DateSeparator({ label }) {
  return (
    <div className="flex items-center justify-center my-3" data-testid="date-separator">
      <span className="text-[11px] font-medium text-[#968F85] bg-white rounded-full px-3 py-1 border border-[#EDECEA]">{label}</span>
    </div>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const queryUser = params.get("user");
  const queryCampaign = params.get("campaign");
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null); // {user_id, name, picture, campaign_title}
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [mobileView, setMobileView] = useState("list"); // list | chat
  const endRef = useRef(null);

  const loadConvos = useCallback(async () => {
    const { data } = await api.get("/messages/conversations");
    setConvos(data);
    return data;
  }, []);

  useEffect(() => {
    (async () => {
      const list = await loadConvos();
      if (queryUser) {
        const existing = list.find((c) => c.user_id === queryUser);
        if (existing) { setActive(existing); }
        else {
          try {
            const { data } = await api.get(`/users/${queryUser}/public`);
            setActive({ user_id: data.user_id, name: data.name, picture: data.picture, role: data.role, is_pro: data.profile?.is_pro });
          } catch { /* ignore */ }
        }
        setMobileView("chat");
      } else if (list.length > 0) {
        setActive(list[0]);
      }
      setLoading(false);
    })();
  }, [loadConvos, queryUser]);

  const loadMessages = useCallback(async (otherId) => {
    const { data } = await api.get(`/messages/${otherId}`);
    setMessages(data);
    // opening marks read → refresh convo list + nav badge
    loadConvos();
    window.dispatchEvent(new Event("unread-refresh"));
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [loadConvos]);

  useEffect(() => { if (active) loadMessages(active.user_id); }, [active, loadMessages]);

  const openConvo = (c) => { setActive(c); setMobileView("chat"); };

  const send = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value || !active || sending) return;
    setSending(true);
    try {
      const payload = { receiver_id: active.user_id, text: value };
      if (queryCampaign && !convos.find((c) => c.user_id === active.user_id)) payload.campaign_id = queryCampaign;
      await api.post("/messages", payload);
      setText("");
      await loadMessages(active.user_id);
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error(err.response?.data?.detail || "Viestin lähetys epäonnistui");
    } finally { setSending(false); }
  };

  if (loading) return <Spinner />;

  return (
    <div data-testid="messages-page">
      <h3 className="text-3xl font-semibold tracking-display text-[#292827] mb-8">Viestit</h3>
      {convos.length === 0 && !active ? (
        <EmptyState title="Ei viestejä vielä" subtitle="Aloita keskustelu hakemuksen tai profiilin kautta." />
      ) : (
        <div className="grid md:grid-cols-[330px_1fr] gap-6 h-[calc(100vh-210px)]">
          {/* Conversation list */}
          <Card className={`p-3 overflow-y-auto ${mobileView === "chat" ? "hidden md:block" : "block"}`}>
            {convos.length === 0 && <p className="p-4 text-sm text-[#968F85]">Ei aiempia keskusteluja.</p>}
            <div className="space-y-1.5">
              {convos.map((c) => {
                const isActive = active?.user_id === c.user_id;
                const hasUnread = c.unread > 0;
                return (
                  <button key={c.user_id} onClick={() => openConvo(c)} data-testid="conversation-item"
                    className={`w-full text-left flex items-center gap-3.5 p-4 rounded-2xl transition-colors duration-200 ${isActive ? "bg-[#EAF2FD]" : hasUnread ? "bg-[#F4F8FD] hover:bg-[#EAF2FD]" : "hover:bg-[#F5F7FA]"}`}>
                    <div className="h-11 w-11 rounded-full bg-[#E9EAEC] text-[#6E675C] flex items-center justify-center font-semibold text-sm shrink-0 overflow-hidden">
                      {c.picture ? <img src={fileUrl(c.picture)} alt="" className="h-full w-full object-cover" /> : initials(c.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-[15px] font-semibold text-[#292827]">{c.name}</p>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[11px] text-[#B3ADA3]" data-testid="convo-time">{fmtConvoTime(c.last_at)}</span>
                          {hasUnread && (
                            <span data-testid="convo-unread-badge"
                              className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#D5E5F9] text-[#292827] text-[11px] font-semibold flex items-center justify-center">
                              {c.unread > 99 ? "99+" : c.unread}
                            </span>
                          )}
                        </div>
                      </div>
                      {c.campaign_title && <p className="mt-0.5 text-[12px] text-[#968F85] truncate">{c.campaign_title}</p>}
                      <p className="mt-1 text-[13px] text-[#B3ADA3] truncate">{c.last_message}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Chat */}
          <Card className={`flex-col overflow-hidden ${mobileView === "list" ? "hidden md:flex" : "flex"}`}>
            {active ? (
              <>
                <div className="flex items-center gap-3.5 px-6 md:px-8 py-5 border-b border-[#EDECEA]">
                  <button onClick={() => setMobileView("list")} className="md:hidden text-[#6E675C] hover:text-[#292827]" data-testid="chat-back-btn"><ArrowLeft size={20} strokeWidth={1.75} /></button>
                  <div className="h-11 w-11 rounded-full bg-[#E9EAEC] text-[#6E675C] flex items-center justify-center font-semibold text-sm overflow-hidden shrink-0">
                    {active.picture ? <img src={fileUrl(active.picture)} alt="" className="h-full w-full object-cover" /> : initials(active.name)}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-[16px] text-[#292827] tracking-tightish truncate">{active.name}</p>
                      {active.role === "creator" && active.is_pro && <ProBadge />}
                    </div>
                    {active.campaign_title && <p className="mt-0.5 text-[13px] text-[#968F85] truncate">{active.campaign_title}</p>}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6 space-y-4 bg-[#F6F8FB]">
                  {messages.length === 0 && <p className="text-center text-sm text-[#968F85] mt-8">Aloita keskustelu lähettämällä viesti.</p>}
                  {messages.map((m, i) => {
                    const mine = m.sender_id === user.user_id;
                    const showSep = i === 0 || isNewDay(messages[i - 1].created_at, m.created_at);
                    return (
                      <div key={m.message_id}>
                        {showSep && <DateSeparator label={dateSeparatorLabel(m.created_at)} />}
                        <div className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid="message-bubble">
                          <div className={`max-w-[72%] rounded-2xl px-5 py-3 text-[15px] leading-relaxed whitespace-pre-wrap break-words ${mine ? "bg-[#292827] text-white" : "bg-white border border-[#EDECEA] text-[#292827]"}`}>
                            {m.text}
                          </div>
                          <span className="mt-1.5 text-[11px] text-[#968F85] px-1">{mine ? "Sinä" : active.name} · {fmtTime(m.created_at)}</span>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>
                <form onSubmit={send} className="flex gap-3 p-5 border-t border-[#EDECEA]">
                  <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Kirjoita viesti..." maxLength={MAX_LEN}
                    data-testid="message-input"
                    className="flex-1 h-[52px] rounded-2xl bg-white border border-[#E6E1D9] px-5 text-[15px] outline-none focus:border-[#292827] transition-colors placeholder:text-[#968F85]" />
                  <button type="submit" disabled={sending || !text.trim()} data-testid="send-message"
                    className={`h-[52px] px-6 rounded-2xl flex items-center gap-2 font-medium transition-colors ${text.trim() ? "bg-[#D5E5F9] text-[#292827] hover:bg-[#BCD6F4]" : "bg-[#E9EAEC] text-[#968F85] cursor-not-allowed"}`}>
                    <Send size={18} strokeWidth={1.75} /> <span className="hidden sm:inline text-sm font-medium">Lähetä</span>
                  </button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-[#968F85]">Valitse keskustelu</div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
